import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { periodLockService } from 'api/services';
import type { CreatePeriodLockRequest } from 'api/types';

const LOCK_SOURCE_OPTIONS: SelectOption[] = [
  { value: 'PERIOD_CLOSE', label: 'Period / Month Close' },
  { value: 'AUDIT', label: 'Audited' },
  { value: 'DAY_END', label: 'Day-End' },
  { value: 'MANUAL', label: 'Manual' },
];

interface LockForm {
  lockFrom: string;
  lockTo: string;
  lockSource: string;
  lockReason: string;
}

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export interface PeriodLockFormModalProps {
  isOpen: boolean;
  onSaved: () => void;
  onClose: () => void;
}

export const PeriodLockFormModal: React.FC<PeriodLockFormModalProps> = ({ isOpen, onSaved, onClose }) => {
  const initial = useCallback((): LockForm => ({ lockFrom: monthStart(), lockTo: today(), lockSource: 'PERIOD_CLOSE', lockReason: '' }), []);
  const [form, setForm] = useState<LockForm>(initial);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const snapshotRef = useRef('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const f = initial();
    setForm(f);
    snapshotRef.current = JSON.stringify(f);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => snapshotRef.current ? JSON.stringify(form) !== snapshotRef.current : false, [form]);
  const update = (patch: Partial<LockForm>) => setForm(prev => ({ ...prev, ...patch }));

  const handleClose = useCallback(() => { if (isDirty) setDiscardOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!form.lockFrom) errors.lockFrom = 'From date is required';
    if (!form.lockTo) errors.lockTo = 'To date is required';
    if (form.lockFrom && form.lockTo && form.lockFrom > form.lockTo) errors.lockTo = 'To date cannot be before From date';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const payload: CreatePeriodLockRequest = {
        lockFrom: form.lockFrom,
        lockTo: form.lockTo,
        lockSource: form.lockSource,
        lockReason: form.lockReason.trim() || undefined,
      };
      await periodLockService.create(payload);
      setSaving(false);
      notificationService.toast.success('Period locked.');
      snapshotRef.current = JSON.stringify(form);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not lock the period.');
    }
  }, [form, onSaved, onClose]);

  if (!isOpen) return null;
  const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Locking…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Lock Period</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="lockFrom">
              <label style={labelStyle}>Lock From <span className="text-danger">*</span></label>
              <input type="date" className={`form-input${fieldErrors.lockFrom ? ' form-field-error' : ''}`} value={form.lockFrom} onChange={(e) => { setFieldErrors(p => ({ ...p, lockFrom: '' })); update({ lockFrom: e.target.value }); }} />
              {fieldErrors.lockFrom && <div className="form-error-message">{fieldErrors.lockFrom}</div>}
            </div>
            <div className="form-group" data-field="lockTo">
              <label style={labelStyle}>Lock To <span className="text-danger">*</span></label>
              <input type="date" className={`form-input${fieldErrors.lockTo ? ' form-field-error' : ''}`} value={form.lockTo} onChange={(e) => { setFieldErrors(p => ({ ...p, lockTo: '' })); update({ lockTo: e.target.value }); }} />
              {fieldErrors.lockTo && <div className="form-error-message">{fieldErrors.lockTo}</div>}
            </div>
            <div className="form-group">
              <SyutSelect label="Source" options={LOCK_SOURCE_OPTIONS} menuPortalTarget={document.body} value={form.lockSource} onChange={(v) => update({ lockSource: (v as string) || 'PERIOD_CLOSE' })} isClearable={false} />
            </div>
            <div className="form-group">
              <label style={labelStyle}>Reason</label>
              <input type="text" className="form-input" value={form.lockReason} onChange={(e) => update({ lockReason: e.target.value })} placeholder="e.g. June audited" />
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Vouchers dated in this range become read-only and no new vouchers can post into it. Unlock from the list to reverse.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={saving}><IconSystem.check size={16} /> Lock Period</button>
        </div>
      </div>

      <ConfirmDialog open={confirmOpen} title="Lock Period"
        message={`Lock ${form.lockFrom} to ${form.lockTo}? Entries in this range will become read-only.`}
        confirmLabel="Lock" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={discardOpen} title="Discard Changes?" message="You have unsaved changes. Discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }} onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

export default PeriodLockFormModal;
